import moment from 'moment';

import {INCIDENT_DURATION_UNIT_MINUTE} from '@constants/variables';
import {
    getEmptyReportData,
    parseReportDuration,
    getDistributionReportCoverage,
    findReportsForDistribution,
    formatReportScopeText,
    parseCountAsDowntime,
    collectAllDistributions,
    isIncidentReport,
} from '@utils/export/reportExportUtils';
import {convertSecondsToMinutesWithOneDecimal} from '@utils/momentWrapper';
import {convertToInt} from '@utils/helpers';

const EMPTY_CONTENT_INFO = {
    contentId: '',
    contentType: '',
    contentVariant: '',
    contentName: '',
    isMainContent: '',
    contributionType: '',
    datacenter: '',
};

const DEFAULT_CONTRIBUTION_TYPE = 'No input';

export const parseCategoryLevel = (categoryLevel) => (
    Array.isArray(categoryLevel) ? categoryLevel.join(' | ') : categoryLevel
);

export const getReportDataForDistribution = (report, event) => {
    if (!report) {
        return getEmptyReportData();
    }

    const reportScope = formatReportScopeText(report);
    const reportTypeName = report.report_type_name || report.report_type?.name || '';
    const incidentSourceName = report.incident_source_name || report.incident_source?.name || '';
    const incidentTypeName = report.incident_type_name || report.incident_type?.name || '';

    return {
        reportType: reportTypeName,
        reportScope,
        incidentSource: isIncidentReport(report) ? incidentSourceName: '',
        incidentType: isIncidentReport(report) ? incidentTypeName: '',
        incidentDuration: isIncidentReport(report) ? parseReportDuration(report, event?.duration) : '',
        countAsDowntime: isIncidentReport(report) ? parseCountAsDowntime(report.count_as_downtime): '',
        comment: report.comment || '',
    };
};

export const getContentInfoForDistribution = (event, distribution) => {
    if (!distribution || !event.contents) return EMPTY_CONTENT_INFO;

    const content = event.contents.find(c => c.event_content_id === distribution.event_content_id);

    if (!content) return EMPTY_CONTENT_INFO;

    const contributionType = event.contribution_types.find(ct => ct.event_content_id === content.event_content_id);
    const encodingDatacenter = event.encoding_datacenters?.find(ed => ed.event_content_id === content.event_content_id);

    return {
        contentId: content.event_content_id || '',
        contentType: content.contentType || '',
        contentVariant: content.eventContentVariant?.short_name || '',
        contentName: content.name || '',
        isMainContent: content.is_main_content ? 'Yes' : 'No',
        contributionType: contributionType?.name || '',
        datacenter: encodingDatacenter?.name || '',
    };
};

export const getProductsInvoiceStatuses = (productsInvoiceStatus, productShortNames) => (
    productShortNames.map(shortName => productsInvoiceStatus[shortName] || '')
);

const getBaseEventData = (event) => [
    event.event_id,
    event.match_or_stage_id,
    `${moment(event.start_datetime).format('YYYY-MM-DD')}`,
    `${moment(event.start_datetime).format('HH:mm')}`,
    `${event.end_datetime ? moment(event.end_datetime).format('YYYY-MM-DD') : ''}`,
    `${event.end_datetime ? moment(event.end_datetime).format('HH:mm') : ''}`,
    parseCategoryLevel(event.sport_or_content_category_level_1),
    parseCategoryLevel(event.category_or_content_category_level_2),
    parseCategoryLevel(event.tournament_or_content_category_level_3),
    event.venue_or_court,
    event.is_st_event || event.stage_id ? event.stage_name_or_description : event.competitors,
    event.description,
    event.external_description,
    event.competitors_warnings?.join(', ') || '',
    event.status,
    event.coverage,
    event.clip_providers_names,
];

const getContentInfoFromEventContent = (eventContent) => {
    if (!eventContent) {
        return EMPTY_CONTENT_INFO;
    }

    return {
        contentId: eventContent.id || '',
        contentType: eventContent.event_content_type?.name || '',
        contentVariant: eventContent.event_content_variant?.short_name || '',
        contentName: eventContent.name || '',
        isMainContent: eventContent.is_main_content ? 'Yes' : 'No',
        contributionType: eventContent.contribution_type?.name || '',
        datacenter: eventContent.encoding_datacenter?.name || '',
    };
};

const getDistributionRowData = (event, eventContent, product, distribution, report) => {
    const contentInfo = getContentInfoFromEventContent(eventContent);
    const reportData = getReportDataForDistribution(report, event);
    let invoiceStatus = '',
        reportedIncidentDuration = '';

    if (distribution && product) {
        invoiceStatus = product.invoice_status_name || 'Undefined';
    }

    if (report?.incident_duration) {
        reportedIncidentDuration = (convertToInt(INCIDENT_DURATION_UNIT_MINUTE) === report.incident_duration_unit_id)
            ? `${convertSecondsToMinutesWithOneDecimal(report.incident_duration_value)}min`
            : report.incident_duration;
    }

    return [
        contentInfo.contentId,
        contentInfo.contentType,
        contentInfo.contentVariant,
        contentInfo.contentName,
        contentInfo.isMainContent,
        contentInfo.contributionType || DEFAULT_CONTRIBUTION_TYPE,
        contentInfo.datacenter,
        distribution?.id || '',
        product?.short_name || '',
        distribution?.distribution_type?.name || '',
        distribution?.product_status?.name || '',
        distribution?.targets ?? '',
        distribution?.bookings ?? '',
        getDistributionReportCoverage(event.report_scope),
        reportData.reportType,
        reportData.reportScope,
        reportData.incidentSource,
        reportData.incidentType,
        reportData.incidentDuration,
        reportedIncidentDuration,
        reportData.countAsDowntime,
        event?.duration ? `${event.duration?.toFixed(1)}min` : '',
        reportData.comment,
        invoiceStatus,
        event.notes || '',
    ];
};


export const getDataRows = (event) => {
    const baseData = getBaseEventData(event);
    const rows = [];

    const eventContents = event.event_contents || event.contents || [];
    const allDistributions = collectAllDistributions(eventContents);

    if (0 === allDistributions.length) {
        const distributionData = getDistributionRowData(event, null, null, null, null);
        rows.push([...baseData, ...distributionData]);

        return rows;
    }

    const reports = event.reports || [];

    allDistributions.forEach(({ eventContent, product, distribution }) => {
        const distributionReports = distribution
            ? findReportsForDistribution(reports, distribution.id)
            : [];

        if (0 === distributionReports.length) {
            const distributionData = getDistributionRowData(event, eventContent, product, distribution, null);
            rows.push([...baseData, ...distributionData]);
        } else {
            distributionReports.forEach(report => {
                const distributionData = getDistributionRowData(event, eventContent, product, distribution, report);
                rows.push([...baseData, ...distributionData]);
            });
        }
    });

    return rows;
};

/**
 * Get columns for the distribution-level export format
 */
export const getColumnsForDistributionRows = [
    // Event-level columns
    'ID',
    'Match ID / Stage ID',
    'Start date',
    'Start time',
    'End date',
    'End time',
    'Sport / 1st level category',
    'Category / 1st stage category / 2nd level category',
    'Tournament / 2nd stage category / 3rd level category',
    'Venue / Court / TV channel',
    'Competitors / Name',
    'Description',
    'External description',
    'Warnings',
    'Status',
    'Coverage',
    'Clip provider',
    'Event content ID',
    'Event content type',
    'Event content variant',
    'Event content name',
    'Is main content',
    'Contribution type',
    'Datacenter',
    'Distribution ID',
    'Product',
    'Distribution type',
    'Product status',
    'Targets',
    'Bookings',
    // 'Notification status', // After notifications implementation
    'Report',
    'Report type',
    'Report scope',
    'Incident source',
    'Incident type',
    'Incident duration in %',
    'Reported incident duration',
    'Downtime',
    'Match duration',
    'Report comment',
    'Invoice status',
    'Notes',
];
